import { useContext } from "react";

import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { TextReviewedCareElement } from "@/Flowsheets/Models/CareElements/TextReviewedCareElement";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

interface ICareElementTextNumberDateReviewedProps {
    isEditable: boolean;
    label: string;
    side: "row" | "column";
    element: TextReviewedCareElement;
    onChange: (element: TextReviewedCareElement) => void;
    options: InlineOption<string>[];
}

/**
 * CareElementOptionsControl component for rendering options control for text reviewed care elements.
 * @param props - The props for the component.
 * @property {TextReviewedCareElement} props.element - The care element to be displayed.
 * @property {function} props.onChange - Callback function to handle changes to the care element.
 * @property {boolean} props.isEditable - Indicates if the care element is editable.
 * @property {string} props.label - The label for the care element.
 * @property {string} props.side - The side on which the care element is displayed (row or column).
 * @property {InlineOption<string>[]} props.options - The options to be displayed in the control.
 * @returns The rendered component.
 */
export function CareElementDateReviewedRadioOptionsControl(
    props: Readonly<ICareElementTextNumberDateReviewedProps>
): JSX.Element {
    const { element, onChange, isEditable, label, side = "row" } = props;
    const { encounterDateTimeWithPrecision } = useContext(EncounterDateContext);

    const isColumn = side === "column";

    return (
        <CareElementDateReviewedRow
            element={element}
            onChange={onChange}
            isEditable={isEditable}
            label={label}
            side={side}
        >
            <InlineOptionsListControl<string>
                id={`${element.Name}-options-control`}
                items={props.options}
                disabled={!isEditable}
                value={element.Value ?? null}
                isMultipleLines={isColumn}
                onChange={option => {
                    const next = element.Value === option.Key ? null : option.Key;

                    const isValid = !!next && !IsNullOrWhiteSpace(next);
                    const shouldAutoFillDate = isValid && !element.DateReviewedWithPrecision?.value;
                    let dateToUse = null;

                    if (isValid) {
                        dateToUse = shouldAutoFillDate
                            ? encounterDateTimeWithPrecision
                            : element.DateReviewedWithPrecision;
                    }

                    onChange({
                        ...element,
                        Value: next,
                        DateReviewedWithPrecision: dateToUse
                    });
                }}
                additionalWrapperClassName="padding-lg-bottom"
            />
        </CareElementDateReviewedRow>
    );
}
